import { useEffect, useRef, useState } from "react";
import { SquareTerminal } from "lucide-react";
import { API, BACKEND } from "@/lib/api";


const MAX_LINES = 200;
const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const PROMPT = "mdh ❯";

const HELP_TEXT = [
    "commands:",
    "  help                     show this help",
    "  clear                    wipe the console  (ctrl+L works too)",
    "  endpoints                list callable endpoints for this app",
    "  GET <path>               read   e.g. GET /apps/self/events",
    "  POST <path> <json>       write  e.g. POST /apps/self/events/test {\"type\":\"demo\"}",
    "  PUT|PATCH|DELETE <path> [json]",
    "paths: \"self\" = this app · /api prefix optional",
    "tips: ↑/↓ command history · tab completes methods & paths · live stream prints between commands",
].join("\n");

export default function ConsolePanel({ appId, events, hooks, live }) {
    const [endpoints, setEndpoints] = useState([]);
    const [lines, setLines] = useState([]);
    const [input, setInput] = useState("");
    const outRef = useRef(null);
    const inputRef = useRef(null);
    const histRef = useRef([]);
    const histIdx = useRef(-1);

    // callable endpoints from this app's own API reference (session-auth ones)
    useEffect(() => {
        let dead = false;
        (async () => {
            try {
                const r = await fetch(`${API}/apps/${appId}/reference`, { credentials: "include" });
                if (!r.ok) throw new Error("failed");
                const d = await r.json();
                const eps = (d.groups || [])
                    .flatMap((g) => g.endpoints || [])
                    .filter((e) => (e.auth || "").includes("session") && e.method !== "WS")
                    .map((e) => ({ method: e.method, path: e.path, desc: e.description || "" }));
                if (!dead) setEndpoints(eps);
            } catch {
                if (!dead) setEndpoints([]);
            }
        })();
        return () => {
            dead = true;
        };
    }, [appId]);

    const push = (entry) =>
        setLines((prev) => [...prev.slice(-MAX_LINES + 1), entry]);

    // boot banner (once per app)
    useEffect(() => {
        setLines([{ kind: "info", text: `mdh console — app ${appId}\ntype "help" for commands, "endpoints" to list this app's API.` }]);
    }, [appId]);

    // incoming live stream → terminal lines
    useEffect(() => {
        const e = events?.[0];
        if (!e) return;
        push({
            kind: "stream",
            text: `< event ${e.type || "?"}` +
                `${e.status != null ? ` ${e.status}` : ""}` +
                `${e.endpoint ? ` ${e.endpoint}` : ""}`,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events]);

    useEffect(() => {
        const h = hooks?.[0];
        if (!h) return;
        push({ kind: "stream", text: `< hook ${h.method || "POST"} ${h.content_type || "payload"}` });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hooks]);

    // autoscroll
    useEffect(() => {
        if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
    }, [lines]);

    const resolvePath = (p) => {
        if (!p.startsWith("/")) return null;
        const aliased = p.replace(/^\/apps\/self(?=\/|$)/, `/apps/${appId}`);
        return aliased.startsWith("/api") ? aliased : `/api${aliased}`;
    };

    const execHttp = async (method, rawPath, bodyStr) => {
        const path = resolvePath(rawPath);
        if (!path) {
            push({ kind: "err", text: `← bad path "${rawPath}" — must start with / (see "endpoints")` });
            return;
        }
        let body;
        if (!["GET", "DELETE"].includes(method) || bodyStr) {
            if (bodyStr) {
                try {
                    JSON.parse(bodyStr);
                    body = bodyStr;
                } catch {
                    push({ kind: "err", text: "← invalid JSON body — check quotes/braces and try again" });
                    return;
                }
            }
        }
        const started = performance.now();
        try {
            const opts = { method, credentials: "include", headers: {} };
            if (body) {
                opts.headers["Content-Type"] = "application/json";
                opts.body = body;
            }
            const r = await fetch(`${BACKEND}${path}`, opts);
            const ms = Math.round(performance.now() - started);
            const text = await r.text();
            let pretty = text;
            try {
                pretty = JSON.stringify(JSON.parse(text), null, 2);
            } catch {
                /* plain text */
            }
            if (pretty.length > 1200) pretty = `${pretty.slice(0, 1200)}\n… (truncated)`;
            push({ kind: r.ok ? "ok" : "err", text: `← ${r.status} · ${ms}ms\n${pretty}` });
        } catch (e) {
            push({ kind: "err", text: `← network error · ${e.message}` });
        }
    };

    const runCommand = (raw) => {
        const cmd = raw.trim();
        if (!cmd) return;
        histRef.current = [cmd, ...histRef.current.filter((h) => h !== cmd)].slice(0, 50);
        histIdx.current = -1;
        push({ kind: "cmd", text: `${PROMPT} ${cmd}` });

        const [head, ...rest] = cmd.split(/\s+/);
        const verb = head.toUpperCase();
        const argLine = cmd.slice(head.length).trim();

        if (verb === "HELP") {
            push({ kind: "info", text: HELP_TEXT });
        } else if (verb === "CLEAR") {
            setLines([]);
        } else if (verb === "ENDPOINTS" || verb === "LS") {
            const listing = endpoints.length
                ? endpoints.map((e) => `  ${e.method.padEnd(7)} ${e.path}${e.desc ? `   # ${e.desc.slice(0, 50)}` : ""}`).join("\n")
                : "  (reference unavailable)";
            push({ kind: "info", text: `callable endpoints (session auth):\n${listing}` });
        } else if (METHODS.includes(verb)) {
            const path = rest[0];
            if (!path) {
                push({ kind: "err", text: `← usage: ${verb} <path> [json] — try "endpoints"` });
                return;
            }
            const bodyStr = argLine.slice(path.length).trim();
            execHttp(verb, path, bodyStr || undefined);
        } else {
            push({ kind: "err", text: `← unknown command "${head}" — type "help"` });
        }
    };

    const complete = () => {
        const val = input;
        const trailSpace = /\s$/.test(val);
        const tokens = val.trimStart().split(/\s+/).filter(Boolean);
        if (!trailSpace && tokens.length <= 1) {
            // complete the method word
            const partial = (tokens[0] || "").toUpperCase();
            const pool = [...METHODS, "HELP", "CLEAR", "ENDPOINTS"].filter((m) => m.startsWith(partial));
            if (pool.length === 1 && partial) setInput(`${pool[0].toLowerCase()} `);
            return;
        }
        const method = (tokens[0] || "").toUpperCase();
        if (!METHODS.includes(method)) return;
        const partial = trailSpace ? "" : tokens[tokens.length - 1];
        const toSelf = (p) => p.replace(/^\/api/, "").replace(`/apps/${appId}`, "/apps/self");
        const aliased = partial.replace(/^\/apps\/self(?=\/|$)/, `/apps/${appId}`);
        const matches = endpoints
            .map((e) => e.path)
            .filter((p) => p.startsWith(aliased) || p.replace(/^\/api/, "").startsWith(aliased));
        if (matches.length === 1) {
            const head = val.slice(0, val.length - partial.length);
            setInput(`${head}${toSelf(matches[0])} `);
        } else if (matches.length > 1) {
            const selfForms = matches.map(toSelf);
            // longest common prefix
            let lcp = selfForms[0];
            for (const m of selfForms.slice(1)) {
                while (!m.startsWith(lcp)) lcp = lcp.slice(0, -1);
            }
            if (lcp.length > partial.length) {
                const head = val.slice(0, val.length - partial.length);
                setInput(head + lcp);
            } else {
                push({ kind: "cmd", text: `${PROMPT} ${val}` });
                push({ kind: "info", text: selfForms.slice(0, 6).map((m) => `  ${m}`).join("\n") });
            }
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            const cmd = input;
            setInput("");
            runCommand(cmd);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const next = Math.min(histIdx.current + 1, histRef.current.length - 1);
            if (histRef.current[next] != null) {
                histIdx.current = next;
                setInput(histRef.current[next]);
            }
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            const next = histIdx.current - 1;
            histIdx.current = next;
            setInput(next >= 0 ? histRef.current[next] : "");
        } else if (e.key === "l" && e.ctrlKey) {
            e.preventDefault();
            setLines([]);
        } else if (e.key === "Tab") {
            e.preventDefault();
            complete();
        }
    };

    return (
        <div data-testid="console-panel" className="glass-panel rounded-xl p-5">
            <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <SquareTerminal className="h-4 w-4 text-cyan-400" />
                    Console
                </h3>
                <span
                    className={`flex items-center gap-1.5 font-mono text-[10px] ${
                        live ? "text-emerald-400" : "text-slate-500"
                    }`}
                >
                    <span
                        className={`h-1.5 w-1.5 rounded-full ${
                            live ? "animate-pulse bg-emerald-400" : "bg-slate-600"
                        }`}
                    />
                    {live ? "streaming" : "offline"}
                </span>
            </div>

            <div
                data-testid="console-output"
                onClick={() => inputRef.current?.focus()}
                className="mt-3 flex h-72 cursor-text flex-col rounded-lg border border-slate-800/80 bg-[#0A0E14] font-mono text-[11px] leading-relaxed"
            >
                <div ref={outRef} className="min-h-0 flex-1 overflow-y-auto p-3.5">
                    {lines.map((l, i) => (
                        <div
                            key={i}
                            data-testid={
                                l.kind === "stream"
                                    ? "console-stream-line"
                                    : l.kind === "cmd"
                                      ? "console-cmd-line"
                                      : `console-response-${i}`
                            }
                            className={`whitespace-pre-wrap break-words ${
                                l.kind === "cmd"
                                    ? "mt-2 text-cyan-300"
                                    : l.kind === "ok"
                                      ? "text-emerald-300"
                                      : l.kind === "err"
                                        ? "text-rose-300"
                                        : l.kind === "info"
                                          ? "text-slate-400"
                                          : "text-slate-500"
                            }`}
                        >
                            {l.text}
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2 border-t border-slate-800/70 px-3.5 py-2.5">
                    <span className="shrink-0 select-none text-cyan-400">{PROMPT}</span>
                    <input
                        ref={inputRef}
                        data-testid="console-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={onKeyDown}
                        spellCheck={false}
                        autoComplete="off"
                        autoCapitalize="off"
                        placeholder='type a command — try "help"'
                        className="w-full bg-transparent text-slate-200 caret-cyan-400 placeholder:text-slate-600 focus:outline-none disabled:opacity-60"
                    />
                </div>
            </div>
        </div>
    );
}
